/**
 * Bundle Help Modals System
 * Generates contextual help modal content for the bundle system
 * Adapted from prototype for Shopify theme integration
 */

class BundleHelpModals {
  /**
   * Understanding Bundle Options
   */
  static bundleOptions() {
    const content = BundleModal.createSection(
      'Bundle Tiers',
      'We offer three pre-configured packages (Standard, Value, Complete) that combine popular add-ons at discounted rates.'
    ) + BundleModal.createSection(
      'Custom Bundle',
      'Prefer to hand-pick individual add-ons? Build a custom bundle tailored exactly to your preferences. Choose from items like Extended Warranty, Personalised Number Plate, and more.'
    ) + BundleModal.createSection(
      'No Bundling',
      'If you only want the main product without any extras, you can add the ride-on to your cart and proceed directly to checkout.'
    );

    return new BundleModal({
      id: 'modal-bundle-options',
      title: 'Understanding Your Options',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * Performance Upgrades Help
   */
  static performanceHelp() {
    const content = BundleModal.createSection(
      'Performance Upgrades',
      'Enhance your ride-on with premium upgrades like EVA soft wheels for smoother rides, upgraded seats for comfort, or advanced remote controls for added safety and convenience.'
    ) + BundleModal.createBulletList([
      '<strong>EVA Soft Wheels:</strong> Puncture-proof, noise-reducing wheels for a smoother and quieter ride',
      '<strong>Leather Seats:</strong> Premium comfort with easy-to-clean materials',
      '<strong>Remote Control:</strong> Parental override for added safety and control'
    ]);

    return new BundleModal({
      id: 'modal-performance-help',
      title: 'Performance Upgrades',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * Personalisation Help
   */
  static personalizationHelp() {
    const content = BundleModal.createSection(
      'Make It Personal',
      'Add a unique touch with personalised number plates and magical letters from Santa, Tooth Fairy, or Easter Bunny.'
    ) + BundleModal.createBulletList([
      '<strong>Number Plates:</strong> Customise with your child\'s name or favourite text (up to 9 characters)',
      '<strong>Front & Back:</strong> Customise each plate separately or use the same text',
      '<strong>Country Flags:</strong> Choose from 45 different flag designs to match their heritage',
      '<strong>Magical Letters:</strong> Personalised letters from beloved characters'
    ]);

    return new BundleModal({
      id: 'modal-personalization-help',
      title: 'Personalisation Options',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * Protection & Warranties Help
   */
  static protectionHelp() {
    const content = BundleModal.createSection(
      'Protect Your Investment',
      'Extend the life of your ride-on with our protection plans and warranties.'
    ) + BundleModal.createBulletList([
      '<strong>Assembly Service:</strong> We assemble and safety-check it in our workshop before it is sent',
      '<strong>Battery Warranty:</strong> Extended coverage for battery replacement',
      '<strong>Extended Warranty:</strong> Comprehensive protection beyond standard coverage'
    ]);

    return new BundleModal({
      id: 'modal-protection-help',
      title: 'Protection & Warranties',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * Gifting addon descriptions - detailed info for each addon
   */
  static giftingAddonDescriptions = {
    'riiroo-gift-bow-in-silk-material-perfect-for-ride-on-car-motorbikes-1': {
      name: 'Large Silk Gift Bow',
      description: 'Premium oversized bow for the perfect big reveal moment - available in red, pink, and blue. Reusable for future celebrations'
    },
    'riiroo-driving-test-and-license-kit': {
      name: 'Driving Test & License Kit',
      description: 'Official RiiRoo driving licence card, multi-page test booklet, and pass certificate - makes them feel like a real driver'
    },
    'kids-traffic-lights-signs-bundle': {
      name: 'Traffic Lights & Signs Bundle',
      description: 'Working traffic light with red/amber/green lights, pedestrian signals, and 5 road signs - turn the garden into a driving course'
    },
    'kids-traffic-light': {
      name: 'Traffic Light',
      description: 'Working traffic light with red/amber/green lights and pedestrian signals - automatic or manual modes with realistic sounds'
    },
    'kids-ride-on-driving-license': {
      name: 'Driving License',
      description: 'Official RiiRoo driving licence card - makes them feel like a real driver'
    },
    'riiroo-genx-16gb-ride-on-car-key-shaped-usb-stick-16gb': {
      name: '16GB Key-Shaped USB Stick',
      description: 'Fun key-shaped USB for storing music - plug in and play their favourite songs through the ride-on speakers'
    },
    'ride-on-car-bluetooth-adapater-aux-in-3-5mm-blue2music-music-adapter': {
      name: 'Bluetooth Adapter',
      description: 'Wireless music streaming via AUX input - play music from any phone or tablet without cables'
    },
    'driving-test-certificate-bundle': {
      name: 'RiiRoo Driving Test Bundle',
      description: 'Official RiiRoo driving test certificate set - available for BMW, Mercedes, and Audi branded ride-ons'
    },
    'bmw-driving-test-certificate-bundle': {
      name: 'BMW Driving Test Bundle',
      description: 'Official BMW-branded driving test certificate, licence card, and pass certificate'
    },
    'mercedes-driving-test-certificate-bundle': {
      name: 'Mercedes Driving Test Bundle',
      description: 'Official Mercedes-branded driving test certificate, licence card, and pass certificate'
    },
    'audi-driving-test-certificate-bundle': {
      name: 'Audi Driving Test Bundle',
      description: 'Official Audi-branded driving test certificate, licence card, and pass certificate'
    },
    'rastar-helmet-for-all-kids-electric-motorbikes-and-quad-bikes': {
      name: 'Rastar Safety Helmet',
      description: 'Premium safety helmet for motorbikes and quads - comfortable fit with adjustable straps'
    },
    'mini-helmet-for-kids-electric-motorbikes-and-quad-bikes': {
      name: 'Mini Safety Helmet',
      description: 'Compact helmet perfect for younger riders on motorbikes and quads'
    },
    'bmw-helmet-for-all-ride-on-motorbikes-and-quads': {
      name: 'BMW Safety Helmet',
      description: 'Official BMW-branded safety helmet for motorbikes and quads - premium quality'
    },
    'volt-racer-helmet-and-knee-pads': {
      name: 'VoltRacer Helmet & Knee Pads',
      description: 'Complete safety set including helmet and knee pads - designed for drift karts'
    }
  };

  /**
   * Gifting & Accessories Help - Dynamic based on eligible addons
   */
  static giftingHelp() {
    // Get eligible gifting addons if bundleAdapter is available
    let bulletPoints = [];

    try {
      if (window.bundleAdapter && window.bundleAdapter.eligibleAddons && window.bundleAdapter.eligibleAddons.gifting) {
        // Dynamic content based on eligible addons
        const eligibleGifting = window.bundleAdapter.eligibleAddons.gifting;

        eligibleGifting.forEach(addon => {
          const desc = this.giftingAddonDescriptions[addon.productHandle];
          if (desc) {
            bulletPoints.push(`<strong>${desc.name}:</strong> ${desc.description}`);
          }
        });
      }
    } catch (e) {
      console.warn('[BundleHelpModals] Could not get eligible addons:', e);
    }

    // Fallback to generic descriptions if no eligible addons found
    if (bulletPoints.length === 0) {
      bulletPoints = [
        '<strong>Large Silk Gift Bow:</strong> Premium oversized bow for the perfect big reveal moment - available in red, pink, and blue',
        '<strong>Driving Test & License Kit:</strong> Official RiiRoo driving licence card, test booklet, and pass certificate - makes them feel like a real driver',
        '<strong>Traffic Lights & Signs:</strong> Working traffic light with red/amber/green lights, pedestrian signals, and 5 road signs - turn the garden into a driving course',
        '<strong>16GB Key-Shaped USB Stick:</strong> Fun key-shaped USB for storing music - plug in and play their favourite songs (for ride-ons with USB port)',
        '<strong>Bluetooth Adapter:</strong> Wireless music streaming via AUX input - play music from any phone or tablet (for ride-ons with AUX input)',
        '<strong>Safety Helmets:</strong> Matching helmets for motorbikes and quads - includes Rastar, Mini, and BMW-branded options'
      ];
    }

    const content = BundleModal.createSection(
      'Complete the Package',
      'Make their gift extra special with presentation accessories and fun add-ons. Available items depend on your ride-on\'s features.'
    ) + BundleModal.createBulletList(bulletPoints);

    return new BundleModal({
      id: 'modal-gifting-help',
      title: 'Gifting & Accessories',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * Number Plate Customisation Help
   */
  static numberPlateHelp() {
    const content = BundleModal.createSection(
      'Create Your Plate',
      'Personalise your number plate with custom text and country flag designs.'
    ) + BundleModal.createBulletList([
      '<strong>Text:</strong> Enter up to 9 characters (letters and numbers only)',
      '<strong>Front & Back:</strong> Customise each plate separately or use the same for both',
      '<strong>Country Flags:</strong> Choose from 45 different flag designs',
    ]) + BundleModal.createSection(
      'Important Information',
      'The number plates come in one size so some modifications may be needed. We can offer a customised plate for a small fee.'
    );

    return new BundleModal({
      id: 'modal-number-plate-help',
      title: 'Number Plate Customisation',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * Magical Letter Creator Help
   */
  static magicalLetterHelp() {
    const content = BundleModal.createSection(
      'Create Magic',
      'Create a personalised letter from Santa, Tooth Fairy, Easter Bunny, or other magical characters.'
    ) + BundleModal.createBulletList([
      '<strong>Choose Sender:</strong> Select from Santa, Tooth Fairy, Easter Bunny, and more',
      '<strong>Child Details:</strong> Enter name, age, town for personalisation',
      '<strong>Achievements:</strong> Highlight accomplishments to celebrate',
      '<strong>Wishes:</strong> Include special wishes for the year ahead',
      '<strong>Nice Things:</strong> Mention kind acts or good behaviour'
    ]) + BundleModal.createSection(
      'Letter Styles',
      'Choose from Formal, Friendly, or Playful tones to match your child\'s personality and age.'
    ) + BundleModal.createSection(
      'Included FREE',
      'This magical keepsake is included FREE with your ride-on order - no extra cost!'
    );

    return new BundleModal({
      id: 'modal-magical-letter-help',
      title: 'Magical Letter Creator',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * Personalised Santa Video Help
   */
  static santaVideoHelp() {
    const content = BundleModal.createSection(
      'Magical Animated Video',
      'We create a beautiful Disney/Pixar-style animated video of Santa visiting YOUR living room with YOUR child\'s new toy! Kids love this charming animated style - just like their favourite movies. Each video is custom-made just for your family.'
    ) + `
      <div style="margin: 20px 0; border-radius: 12px; overflow: hidden; background: #000; min-height: 0; height: auto; align-self: start;">
        <video
          autoplay
          loop
          muted
          playsinline
          style="width: 100%; display: block; aspect-ratio: 16/9; object-fit: cover; min-height: 0; height: auto;">
          <source src="https://cdn.shopify.com/videos/c/o/v/d150759c29aa4248aa51c7c65329ad82.mp4" type="video/mp4">
          Your browser doesn't support video playback.
        </video>
      </div>
    ` + BundleModal.createSection(
      'How It Works',
      ''
    ) + BundleModal.createBulletList([
      '<strong>Upload a Photo:</strong> Send us a picture of your living room where the toy will be',
      '<strong>Animation Style:</strong> Your photo becomes a charming Disney/Pixar-style animated scene',
      '<strong>Video Generation:</strong> AI creates a personalised video of Santa in your home',
      '<strong>Email Delivery:</strong> Your magical video is delivered straight to your inbox'
    ]) + BundleModal.createSection(
      'What\'s Included',
      ''
    ) + BundleModal.createBulletList([
      '<strong>Fully Personalised:</strong> Your child\'s name, your living room, their actual toy',
      '<strong>HD Quality:</strong> 1080p high-definition video (8 seconds)',
      '<strong>Digital Delivery:</strong> Sent via email in MP4 format',
      '<strong>Fast Turnaround:</strong> Free 3-5 days, Next Day available'
    ]) + BundleModal.createSection(
      'Perfect For',
      'This magical keepsake is perfect for creating unforgettable Christmas memories. Imagine your child\'s face when they see Santa in their own living room, putting a beautiful bow on their special gift!'
    ) + `
      ${window.santaVideoState?.product?.compareAtPrice && window.santaVideoState.product.compareAtPrice > window.santaVideoState.product.price ? `
      <div style="margin-top: 20px; padding: 16px; background: linear-gradient(135deg, rgb(var(--color-primary) / 0.1) 0%, rgb(var(--color-primary) / 0.05) 100%); border-radius: 8px; border-left: 4px solid rgb(var(--color-primary)); display: flex; align-items: flex-start; gap: 12px;">
        <svg style="width: 24px; height: 24px; color: rgb(var(--color-primary)); flex-shrink: 0; margin-top: 2px;" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clip-rule="evenodd"/>
        </svg>
        <div>
          <p style="margin: 0; font-weight: 600; color: rgb(var(--color-drawer-text));">
            Special Launch Price: £${window.santaVideoState.product.price.toFixed(0)} (normally £${window.santaVideoState.product.compareAtPrice.toFixed(0)})
          </p>
          <p style="margin: 8px 0 0 0; font-size: 0.9em; color: rgb(var(--color-drawer-text) / 0.8);">
            Limited time offer - save £${(window.santaVideoState.product.compareAtPrice - window.santaVideoState.product.price).toFixed(0)} on this magical Christmas keepsake!
          </p>
        </div>
      </div>
      ` : ''}
    `;

    return new BundleModal({
      id: 'modal-santa-video-help',
      title: 'Personalised Santa Video',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * Parental Remote Control Help
   */
  static parentalRemoteHelp() {
    const content = BundleModal.createSection(
      'Stay in Control',
      'The parental remote control gives you full override capability, letting you take control whenever needed while your child builds confidence and learns to drive.'
    ) + BundleModal.createBulletList([
      '<strong>Full Override:</strong> Take complete control at any moment with the parental remote',
      '<strong>Safety First:</strong> Perfect for younger drivers who are still learning',
      '<strong>Build Confidence:</strong> Let them drive while you maintain ultimate control',
      '<strong>2.4GHz Technology:</strong> Reliable 15-30 metre range with full directional control',
      '<strong>Emergency Stop:</strong> Instantly stop the vehicle if needed'
    ]) + BundleModal.createSection(
      'Perfect For',
      'Ideal for parents who want peace of mind while their children enjoy independent driving. Great for younger kids (3-5 years) or first-time drivers.'
    );

    return new BundleModal({
      id: 'modal-parental-remote-help',
      title: 'Parental Remote Control',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * EVA Wheels Help
   */
  static evaWheelsHelp() {
    const content = BundleModal.createSection(
      'Superior Comfort & Quiet',
      'Upgrade from noisy plastic wheels to premium EVA soft wheels for a dramatically smoother, quieter ride that works beautifully indoors and outdoors.'
    ) + BundleModal.createBulletList([
      '<strong>Whisper-Quiet:</strong> Dramatically reduces noise compared to hard plastic wheels',
      '<strong>Buttery Smooth:</strong> Absorbs bumps and provides cushioned rides on any surface',
      '<strong>Indoor & Outdoor:</strong> Perfect for smooth floors, pavement, grass, and gravel',
      '<strong>Puncture-Proof:</strong> Solid EVA foam never goes flat or needs maintenance',
      '<strong>Longer Lasting:</strong> Won\'t wear down or crack like plastic wheels'
    ]) + BundleModal.createSection(
      'What Parents Love',
      'Parents rave about how much quieter EVA wheels are indoors - no more loud clicking on hardwood floors! Kids love the smoother ride that feels more like a real car.'
    );

    return new BundleModal({
      id: 'modal-eva-wheels-help',
      title: 'EVA Soft Wheels Upgrade',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * Leather Seat Help
   */
  static leatherSeatHelp() {
    const content = BundleModal.createSection(
      'Premium Comfort',
      'Upgrade to a padded leather-style seat for superior comfort during longer adventures, plus the premium look parents love.'
    ) + BundleModal.createBulletList([
      '<strong>Extra Padding:</strong> Plush cushioning for longer, more comfortable rides',
      '<strong>Leather-Look Finish:</strong> Premium appearance that elevates the whole ride-on',
      '<strong>Easy to Clean:</strong> Wipes clean in seconds - spills and dirt come right off',
      '<strong>Durable Material:</strong> Resistant to tears, scratches, and wear',
      '<strong>Better Support:</strong> Ergonomic design supports proper posture'
    ]) + BundleModal.createSection(
      'The Difference',
      'Standard seats are basic hard plastic. The leather-style upgrade adds substantial padding and a premium finish that makes the ride-on look and feel more luxurious.'
    );

    return new BundleModal({
      id: 'modal-leather-seat-help',
      title: 'Padded Leather-Style Seat',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * Helmet & Goggles Sizing Help
   */
  static helmetGogglesSizingHelp() {
    const content = BundleModal.createSection(
      'Finding the Perfect Fit',
      'A properly fitted helmet is essential for safety. Use a soft tape measure around the widest part of the head, just above the eyebrows and ears.'
    ) + `
      <div style="margin: 16px 0; padding: 16px; background: rgb(var(--color-primary) / 0.05); border-radius: 12px; border: 1px solid rgb(var(--color-primary) / 0.2);">
        <h4 style="margin: 0 0 12px 0; font-size: 0.95rem; font-weight: 700; color: rgb(var(--color-drawer-text));">
          Kids Helmet Sizing Guide
        </h4>
        <table style="width: 100%; border-collapse: collapse; font-size: 0.85rem;">
          <thead>
            <tr style="border-bottom: 2px solid rgb(var(--color-primary) / 0.3);">
              <th style="text-align: left; padding: 8px 4px; font-weight: 600; color: rgb(var(--color-drawer-text));">Size</th>
              <th style="text-align: left; padding: 8px 4px; font-weight: 600; color: rgb(var(--color-drawer-text));">Head Circumference</th>
              <th style="text-align: left; padding: 8px 4px; font-weight: 600; color: rgb(var(--color-drawer-text));">Typical Age</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border-bottom: 1px solid rgb(var(--color-drawer-text) / 0.1);">
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">XS</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">48-50 cm</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.7);">3-5 years</td>
            </tr>
            <tr style="border-bottom: 1px solid rgb(var(--color-drawer-text) / 0.1);">
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">S</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">50-52 cm</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.7);">5-7 years</td>
            </tr>
            <tr style="border-bottom: 1px solid rgb(var(--color-drawer-text) / 0.1);">
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">M</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">52-54 cm</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.7);">7-10 years</td>
            </tr>
            <tr>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">L</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">54-56 cm</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.7);">10+ years</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style="margin: 16px 0; padding: 16px; background: rgb(var(--color-primary) / 0.05); border-radius: 12px; border: 1px solid rgb(var(--color-primary) / 0.2);">
        <h4 style="margin: 0 0 12px 0; font-size: 0.95rem; font-weight: 700; color: rgb(var(--color-drawer-text));">
          Adult Helmet Sizing Guide
        </h4>
        <table style="width: 100%; border-collapse: collapse; font-size: 0.85rem;">
          <thead>
            <tr style="border-bottom: 2px solid rgb(var(--color-primary) / 0.3);">
              <th style="text-align: left; padding: 8px 4px; font-weight: 600; color: rgb(var(--color-drawer-text));">Size</th>
              <th style="text-align: left; padding: 8px 4px; font-weight: 600; color: rgb(var(--color-drawer-text));">Head Circumference</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border-bottom: 1px solid rgb(var(--color-drawer-text) / 0.1);">
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">S</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">55-56 cm</td>
            </tr>
            <tr style="border-bottom: 1px solid rgb(var(--color-drawer-text) / 0.1);">
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">M</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">57-58 cm</td>
            </tr>
            <tr style="border-bottom: 1px solid rgb(var(--color-drawer-text) / 0.1);">
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">L</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">59-60 cm</td>
            </tr>
            <tr>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">XL</td>
              <td style="padding: 8px 4px; color: rgb(var(--color-drawer-text) / 0.9);">61-62 cm</td>
            </tr>
          </tbody>
        </table>
      </div>
    ` + BundleModal.createSection(
      'How to Measure',
      ''
    ) + BundleModal.createBulletList([
      '<strong>Step 1:</strong> Use a soft measuring tape (or string that you measure afterwards)',
      '<strong>Step 2:</strong> Wrap it around the head about 1 inch above the eyebrows',
      '<strong>Step 3:</strong> Keep the tape level around the widest part of the head',
      '<strong>Step 4:</strong> Note the measurement in centimetres'
    ]) + BundleModal.createSection(
      'Proper Fit Check',
      ''
    ) + BundleModal.createBulletList([
      '<strong>Snug but comfortable:</strong> Should fit firmly without being too tight',
      '<strong>Level position:</strong> Sits level on the head, not tilted back',
      '<strong>Two-finger rule:</strong> Two fingers should fit between chin strap and chin',
      '<strong>No movement:</strong> Helmet shouldn\'t rock side to side or front to back'
    ]) + BundleModal.createSection(
      'Goggles Included',
      'Each bundle includes matching goggles that fit securely with the helmet. The goggles provide eye protection from dust, debris, and wind while riding.'
    );

    return new BundleModal({
      id: 'modal-helmet-goggles-sizing-help',
      title: 'Helmet & Goggles Sizing Guide',
      content: content,
      style: 'bottom-sheet'
    });
  }

  /**
   * Initialize all help modals
   */
  static initializeAll() {
    const modals = [
      this.bundleOptions(),
      this.performanceHelp(),
      this.personalizationHelp(),
      this.protectionHelp(),
      this.giftingHelp(),
      this.numberPlateHelp(),
      this.magicalLetterHelp(),
      this.santaVideoHelp(),
      this.parentalRemoteHelp(),
      this.evaWheelsHelp(),
      this.leatherSeatHelp(),
      this.helmetGogglesSizingHelp()
    ];

    // Get the drawer__inner element (same pattern as cart-drawer)
    const bundleDrawer = document.getElementById('BundleDrawer');
    if (!bundleDrawer) {
      console.error('[BundleHelpModals] BundleDrawer element not found');
      return;
    }

    const drawerInner = bundleDrawer.querySelector('.drawer__inner');
    if (!drawerInner) {
      console.error('[BundleHelpModals] drawer__inner element not found');
      return;
    }

    // Remove existing modals
    modals.forEach(modal => {
      const existingModal = document.getElementById(modal.id);
      if (existingModal) {
        existingModal.remove();
      }
    });

    // Create and append modals INSIDE drawer__inner (same as cart-drawer pattern)
    modals.forEach(modal => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = modal.render();
      const modalElement = tempDiv.firstElementChild;
      drawerInner.appendChild(modalElement);
    });
  }

  /**
   * Show specific help modal by ID
   * Regenerates dynamic modals (like gifting) before showing
   */
  static show(modalId) {
    // Regenerate gifting modal content dynamically each time it's shown
    if (modalId === 'modal-gifting-help') {
      const existingModal = document.getElementById(modalId);
      if (existingModal) {
        existingModal.remove();
      }

      // Regenerate with current eligible addons
      const modal = this.giftingHelp();
      const bundleDrawer = document.getElementById('BundleDrawer');
      if (bundleDrawer) {
        const drawerInner = bundleDrawer.querySelector('.drawer__inner');
        if (drawerInner) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = modal.render();
          const modalElement = tempDiv.firstElementChild;
          drawerInner.appendChild(modalElement);
        }
      }
    }

    BundleModal.show(modalId);
  }
}

// Export to global scope
window.BundleHelpModals = BundleHelpModals;

// Initialize modals when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => BundleHelpModals.initializeAll());
} else {
  BundleHelpModals.initializeAll();
}
